import { useEffect, useMemo, useState } from "react";

import PropTypes from "prop-types";

import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Icon from "@mui/material/Icon";

import SoftBox from "components/SoftBox";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import SoftSelect from "components/SoftSelect";

import Swal from "sweetalert2";

import { getData, postData } from "Utils/server";
import { getAuthorizationHeader, getUserData } from "Utils/helpers";

const WIZARD_STEPS = {
  PLATFORM: 0,
  ENTITIES: 1,
  USER: 2,
};

const SUPER_ADMIN_ROLE_ID = 1;
const DEFAULT_SLUG = "new-platform";

const normalizeText = (value) => {
  if (!value) return "";

  return value
    .toString()
    .toLowerCase()
    .replace(/[\s_-]+/g, " ")
    .trim();
};

const slugifyValue = (value) => {
  if (!value) return "";

  return value
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
};

const capitalizeFirstLetter = (value) => {
  if (!value) return "";

  const trimmedValue = value.toString().trim();
  if (!trimmedValue) return "";

  return `${trimmedValue.charAt(0).toUpperCase()}${trimmedValue.slice(1)}`;
};

const normalizeProviderToken = (value) => {
  if (!value) return "";

  return value
    .toString()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
};

const getProviderDisplayName = (providerOption) => {
  if (!providerOption) return "provider";

  return providerOption.label || providerOption.name || providerOption.value || "provider";
};

const buildDemoFilterName = (providerLabel) => {
  const safeProviderToken = providerLabel
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

  return `demo_filter_${safeProviderToken || "provider"}`;
};

const findPreferredDemoProvider = (selectedProviderOptions) => {
  if (!Array.isArray(selectedProviderOptions) || selectedProviderOptions.length === 0) return null;

  const smProvider = selectedProviderOptions.find((providerOption) => {
    const providerName = getProviderDisplayName(providerOption);
    return normalizeProviderToken(providerName) === "sm";
  });

  return smProvider || selectedProviderOptions[0];
};

const escapeHtml = (value) => {
  if (value == null) return "";

  return value
    .toString()
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\"/g, "&quot;")
    .replace(/'/g, "&#039;");
};

const ensureTrailingSlash = (value) => {
  if (!value) return "";
  return value.endsWith("/") ? value : `${value}/`;
};

const buildSuccessHtml = ({ backofficeUrl, username, password, apiKey, docsUrl }) => {
  const safeBackofficeUrl = escapeHtml(backofficeUrl);
  const safeUsername = escapeHtml(username);
  const safePassword = escapeHtml(password);
  const safeApiKey = escapeHtml(apiKey);
  const safeDocsUrl = escapeHtml(docsUrl);

  return `
    <div style="display:flex; gap:24px; text-align:left; font-size:14px; justify-content:space-between;">
      <div style="flex:1; border:1px solid #e9ecef; border-radius:8px; padding:12px;">
        <div style="font-weight:700; margin-bottom:10px;">Backoffice Credentials:</div>
        <div style="margin-bottom:8px;"><b>URL:</b> ${safeBackofficeUrl}</div>
        <div style="margin-bottom:8px;"><b>Username:</b> ${safeUsername}</div>
        <div style="margin-bottom:8px;"><b>Password:</b> ${safePassword}</div>
      </div>
      <div style="flex:1; border:1px solid #e9ecef; border-radius:8px; padding:12px;">
        <div style="font-weight:700; margin-bottom:10px;">API Credentials:</div>
        <div style="margin-bottom:8px;"><b>API Key:</b> ${safeApiKey}</div>
        <div style="margin-bottom:8px;"><b>Documentation:</b> ${safeDocsUrl}</div>
      </div>
    </div>
  `;
};

const generateRandomPassword = (length = 12) => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%*";
  let password = "";

  for (let index = 0; index < length; index++) {
    password += chars.charAt(Math.floor(Math.random() * chars.length));
  }

  return password;
};

const getGeneratedPlatformValues = (platformName) => {
  const slug = slugifyValue(platformName) || DEFAULT_SLUG;

  return {
    exchangeName: `${slug}-feed`,
    queueIdentifier: slug,
    vlcourtEmail: `${slug}@skill90.io`,
    vlcourtPassword: `${slug}123!`,
  };
};

const getGeneratedUserValues = (platformName, username) => {
  const usernameSlug = slugifyValue(username) || slugifyValue(platformName) || DEFAULT_SLUG;
  const firstName = capitalizeFirstLetter(platformName || "Platform");
  const lastName = "Demo";

  return {
    firstName,
    lastName,
    username: usernameSlug,
    email: `${usernameSlug}@skill90.io`,
  };
};

const getCreatedEntityId = (response) => {
  if (!response || !response.data) return null;

  if (response.data.id) return response.data.id;
  if (Array.isArray(response.data) && response.data.length > 0) return response.data[0].id;

  return null;
};

const findMasterClientRole = (roles) => {
  if (!Array.isArray(roles)) return null;

  return roles.find((role) => {
    const roleName = normalizeText(role.name);

    if (!roleName) return false;

    return roleName.includes("master client") || (roleName.includes("master") && roleName.includes("client"));
  });
};

function CreatePlatformWizard({ showModal, signalCloseModal }) {
  const [activeStep, setActiveStep] = useState(WIZARD_STEPS.PLATFORM);
  const [platformName, setPlatformName] = useState("");
  const [demoFeed, setDemoFeed] = useState(true);
  const [settleOnlyBets, setSettleOnlyBets] = useState(true);
  const [validateBets, setValidateBets] = useState(true);

  const [rmqOptions, setRmqOptions] = useState([]);
  const [providerOptions, setProviderOptions] = useState([]);
  const [selectedRmqs, setSelectedRmqs] = useState([]);
  const [selectedProviders, setSelectedProviders] = useState([]);
  const [providerSportsMap, setProviderSportsMap] = useState({});

  const [userUsername, setUserUsername] = useState("");
  const [userPassword, setUserPassword] = useState("");
  const [masterClientRole, setMasterClientRole] = useState(null);
  const [masterClientPermissions, setMasterClientPermissions] = useState([]);

  const [loadingData, setLoadingData] = useState(false);
  const [saving, setSaving] = useState(false);

  const editorData = getUserData();

  const generatedPlatformValues = useMemo(
    () => getGeneratedPlatformValues(platformName),
    [platformName]
  );

  const generatedUserValues = useMemo(
    () => getGeneratedUserValues(platformName, userUsername),
    [platformName, userUsername]
  );

  const resetWizard = () => {
    setActiveStep(WIZARD_STEPS.PLATFORM);
    setPlatformName("");
    setDemoFeed(true);
    setSettleOnlyBets(true);
    setValidateBets(true);
    setSelectedRmqs([]);
    setSelectedProviders([]);
    setProviderSportsMap({});
    setUserUsername("");
    setUserPassword("");
    setMasterClientRole(null);
    setMasterClientPermissions([]);
  };

  const loadEntities = async () => {
    setLoadingData(true);

    const isSuperAdmin =
      editorData &&
      Array.isArray(editorData.roles) &&
      editorData.roles.some((role) => Number(role.id) === SUPER_ADMIN_ROLE_ID);

    const permissionsEndpoint =
      isSuperAdmin || !editorData || !editorData.id
        ? `/api/v1/entities/permissions`
        : `/api/v1/users/${editorData.id}/permissions`;

    const rmqResponse = await getData(`/api/v1/entities/availableRmqClusters`, {
      Authorization: getAuthorizationHeader(),
    });

    const templatesResponse = await getData(`/api/v1/templates/unis`, {
      Authorization: getAuthorizationHeader(),
    });

    const rolesResponse = await getData(`/api/v1/roles`, {
      Authorization: getAuthorizationHeader(),
    });

    const permissionsResponse = await getData(permissionsEndpoint, {
      Authorization: getAuthorizationHeader(),
    });

    if (
      !rmqResponse ||
      !templatesResponse ||
      !templatesResponse.data ||
      !rolesResponse ||
      !permissionsResponse
    ) {
      setLoadingData(false);
      Swal.fire("Error!", "Unable to load wizard data. Please try again.", "error");
      return;
    }

    const rmqList = Array.isArray(rmqResponse.data)
      ? rmqResponse.data.map((rmq) => ({ value: rmq.id, label: rmq.name }))
      : [];

    const providers = Array.isArray(templatesResponse.data.providers)
      ? templatesResponse.data.providers
      : [];
    const sports = Array.isArray(templatesResponse.data.sports) ? templatesResponse.data.sports : [];

    const providersList = providers
      .map((provider) => ({
        value: provider.id,
        label: provider.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    const uniqueSports = Array.from(new Set(sports.map((sport) => sport.id))).map((sportId) => ({
      id: sportId,
    }));

    const sportsPerProvider = providers.reduce((accumulator, provider) => {
      accumulator[provider.id] = uniqueSports;
      return accumulator;
    }, {});

    const roles = Array.isArray(rolesResponse.data) ? rolesResponse.data : [];
    const masterRole = findMasterClientRole(roles);
    const permissionsList = Array.isArray(permissionsResponse.data) ? permissionsResponse.data : [];

    const rolePermissions = masterRole
      ? permissionsList.filter((permission) => Number(permission.permission_role) >= Number(masterRole.id))
      : [];
    const rolePermissionsToAssign = rolePermissions.length > 0 ? rolePermissions : permissionsList;

    setRmqOptions(rmqList);
    setProviderOptions(providersList);
    setProviderSportsMap(sportsPerProvider);
    setMasterClientRole(masterRole);
    setMasterClientPermissions(rolePermissionsToAssign);
    setLoadingData(false);
  };

  const resolveCreatedPlatform = async (platformResponse, createdPlatformName) => {
    const responsePlatformId = getCreatedEntityId(platformResponse);
    if (!editorData || !editorData.id) {
      return responsePlatformId ? { id: responsePlatformId, name: createdPlatformName } : null;
    }

    const platformsResponse = await getData(`/api/v1/users/${editorData.id}/platforms`, {
      Authorization: getAuthorizationHeader(),
    });

    if (!platformsResponse || !Array.isArray(platformsResponse.data)) {
      return responsePlatformId ? { id: responsePlatformId, name: createdPlatformName } : null;
    }

    let platformById = null;
    if (responsePlatformId) {
      platformById = platformsResponse.data.find(
        (platformItem) => Number(platformItem.id) === Number(responsePlatformId)
      );
    }

    if (platformById) return platformById;

    const matchingPlatforms = platformsResponse.data
      .filter((platformItem) => normalizeText(platformItem.name) === normalizeText(createdPlatformName))
      .sort((a, b) => Number(b.id) - Number(a.id));

    if (matchingPlatforms.length === 0) {
      return responsePlatformId ? { id: responsePlatformId, name: createdPlatformName } : null;
    }

    return matchingPlatforms[0];
  };

  useEffect(() => {
    if (showModal) {
      resetWizard();
      loadEntities();
    }
  }, [showModal]);

  const handleClose = () => {
    if (saving) return;

    resetWizard();
    signalCloseModal(false, null);
  };

  const validatePlatformStep = () => {
    if (!platformName || !platformName.trim()) {
      Swal.fire("Warning!", "Please provide Platform Name to continue.", "warning");
      return false;
    }

    return true;
  };

  const validateEntitiesStep = () => {
    if (!selectedProviders || selectedProviders.length === 0) {
      Swal.fire("Warning!", "Please select at least one Provider.", "warning");
      return false;
    }

    return true;
  };

  const validateUserStep = () => {
    if (!userUsername || !userUsername.trim()) {
      Swal.fire("Warning!", "Please provide Username.", "warning");
      return false;
    }

    if (!userPassword || userPassword.length < 6) {
      Swal.fire("Warning!", "Password must be at least 6 characters.", "warning");
      return false;
    }

    if (!masterClientRole) {
      Swal.fire("Warning!", "Master client role was not found. Please contact support.", "warning");
      return false;
    }

    return true;
  };

  const handleNext = () => {
    if (activeStep === WIZARD_STEPS.PLATFORM) {
      if (!validatePlatformStep()) return;

      setActiveStep(WIZARD_STEPS.ENTITIES);
      return;
    }

    if (activeStep === WIZARD_STEPS.ENTITIES) {
      if (!validateEntitiesStep()) return;

      setUserUsername(slugifyValue(platformName) || DEFAULT_SLUG);
      setUserPassword(generateRandomPassword(12));

      setActiveStep(WIZARD_STEPS.USER);
    }
  };

  const handleBack = () => {
    if (activeStep === WIZARD_STEPS.USER) {
      setActiveStep(WIZARD_STEPS.ENTITIES);
      return;
    }

    setActiveStep(WIZARD_STEPS.PLATFORM);
  };

  const buildFilters = (providerIds) => {
    return providerIds.map((providerId) => {
      const providerSports = providerSportsMap[providerId] || [];

      return {
        provider: {
          id: providerId,
          inplay: true,
          pregame: true,
        },
        sports: providerSports.map((sport) => ({
          id: sport.id,
          inplay: true,
          pregame: true,
          default: true,
        })),
        competitions: [],
      };
    });
  };

  const createDemoTemplateAndFilter = async (platformId) => {
    const preferredDemoProvider = findPreferredDemoProvider(selectedProviders);

    if (!preferredDemoProvider || preferredDemoProvider.value == null) {
      return {
        ok: false,
        message: "cannot resolve provider for DEMOTemplate filter.",
      };
    }

    const createTemplateResponse = await postData(
      `/api/v1/templates`,
      {
        platform_id: platformId,
        name: "DEMOTemplate",
      },
      {
        Authorization: getAuthorizationHeader(),
      }
    );

    const createdTemplateId = getCreatedEntityId(createTemplateResponse);

    if (!createTemplateResponse || !createdTemplateId) {
      return {
        ok: false,
        message: "DEMOTemplate could not be created.",
      };
    }

    const availableFiltersResponse = await getData(
      `/api/v1/templates/${createdTemplateId}/available-filters`,
      {
        Authorization: getAuthorizationHeader(),
      }
    );

    const availableFiltersData = availableFiltersResponse ? availableFiltersResponse.data : null;
    const availableProviders =
      availableFiltersData && Array.isArray(availableFiltersData.providers)
        ? availableFiltersData.providers
        : [];
    const availableSports =
      availableFiltersData && Array.isArray(availableFiltersData.sports) ? availableFiltersData.sports : [];

    if (availableProviders.length === 0 || availableSports.length === 0) {
      return {
        ok: false,
        message: "DEMOTemplate was created but no available providers/sports were returned for filter setup.",
      };
    }

    const hasSmInSelection = selectedProviders.some((providerOption) => {
      const providerName = getProviderDisplayName(providerOption);
      return normalizeProviderToken(providerName) === "sm";
    });

    let preferredProviderFromTemplate = null;

    if (hasSmInSelection) {
      preferredProviderFromTemplate = availableProviders.find((providerItem) => {
        return normalizeProviderToken(providerItem.name) === "sm";
      });
    }

    if (!preferredProviderFromTemplate) {
      for (let index = 0; index < selectedProviders.length; index++) {
        const selectedProvider = selectedProviders[index];
        const providerMatchById = availableProviders.find(
          (providerItem) => Number(providerItem.id) === Number(selectedProvider.value)
        );

        if (providerMatchById) {
          preferredProviderFromTemplate = providerMatchById;
          break;
        }
      }
    }

    if (!preferredProviderFromTemplate) {
      const preferredProviderName = getProviderDisplayName(preferredDemoProvider);
      preferredProviderFromTemplate = availableProviders.find((providerItem) => {
        return normalizeProviderToken(providerItem.name) === normalizeProviderToken(preferredProviderName);
      });
    }

    if (!preferredProviderFromTemplate) {
      return {
        ok: false,
        message: "DEMOTemplate was created but no matching provider was found for demo filter.",
      };
    }

    const providerSports = availableSports
      .filter((sportItem) => Number(sportItem.provider_id) === Number(preferredProviderFromTemplate.id))
      .filter((sportItem) => sportItem.available !== false);

    const uniqueProviderSports = Array.from(
      providerSports.reduce((accumulator, sportItem) => {
        if (!accumulator.has(Number(sportItem.id))) {
          accumulator.set(Number(sportItem.id), sportItem);
        }

        return accumulator;
      }, new Map()).values()
    );

    if (uniqueProviderSports.length === 0) {
      return {
        ok: false,
        message: `DEMOTemplate was created but no sports were found for provider ${preferredProviderFromTemplate.name}.`,
      };
    }

    const providerLabel = preferredProviderFromTemplate.name || getProviderDisplayName(preferredDemoProvider);
    const demoFilterName = buildDemoFilterName(providerLabel);
    const baseFilter = {
      provider: {
        id: Number(preferredProviderFromTemplate.id),
        inplay: true,
        pregame: true,
      },
      sports: uniqueProviderSports.map((sportItem) => ({
        id: Number(sportItem.id),
        inplay: true,
        pregame: true,
        default: true,
      })),
      competitions: [],
      oddsConfig: [],
      marketsConfig: [],
    };

    const createFilter = async (includeName) => {
      const filterPayload = {
        templateId: createdTemplateId,
        filters: [
          {
            ...(includeName ? { name: demoFilterName } : {}),
            ...baseFilter,
          },
        ],
      };

      return postData(`/api/v1/templates/${createdTemplateId}/filters`, filterPayload, {
        Authorization: getAuthorizationHeader(),
      });
    };

    let createFilterResponse = await createFilter(true);

    if (!createFilterResponse) {
      createFilterResponse = await createFilter(false);
    }

    if (!createFilterResponse) {
      return {
        ok: false,
        message: `DEMOTemplate was created but ${demoFilterName} failed to save.`,
      };
    }

    const createdFiltersResponse = await getData(`/api/v1/templates/${createdTemplateId}/filters`, {
      Authorization: getAuthorizationHeader(),
    });

    const createdFilters =
      createdFiltersResponse && Array.isArray(createdFiltersResponse.data) ? createdFiltersResponse.data : [];

    const createdFilter = createdFilters.find((filterItem) => {
      const filterProviderId =
        filterItem.provider_id || filterItem.providerId || (filterItem.provider ? filterItem.provider.id : null);

      return Number(filterProviderId) === Number(preferredProviderFromTemplate.id);
    });

    if (!createdFilter) {
      return {
        ok: false,
        message: `DEMOTemplate was created but ${demoFilterName} was not persisted.`,
      };
    }

    return {
      ok: true,
      templateId: createdTemplateId,
      filterName: createdFilter.name || demoFilterName,
      providerName: providerLabel,
    };
  };

  const handleCreatePlatformAndUser = async () => {
    if (!validatePlatformStep() || !validateEntitiesStep() || !validateUserStep()) return;

    const filters = buildFilters(selectedProviders.map((provider) => provider.value));

    if (!filters.some((filterItem) => filterItem.sports && filterItem.sports.length > 0)) {
      Swal.fire(
        "Warning!",
        "No sports found for selected providers. Please try again or contact support.",
        "warning"
      );
      return;
    }

    setSaving(true);

    const platformPayload = {
      name: platformName.trim(),
      exchange_name: generatedPlatformValues.exchangeName,
      vlcourt_email: generatedPlatformValues.vlcourtEmail,
      vlcourt_password: generatedPlatformValues.vlcourtPassword,
      queue_identifier: generatedPlatformValues.queueIdentifier,
      routing_key: null,
      rmqs_selected: selectedRmqs.length > 0 ? selectedRmqs.map((item) => item.label).join(",") : null,
      settle_only_bets: settleOnlyBets,
      validate_bets: validateBets,
      receive_seperate_statistics: false,
      risk_management: false,
      demo: demoFeed,
      hard_provider_down: false,
      nu_remove: false,
      compress_message: false,
      trader_view: false,
      promos: false,
      outrights: 3,
      filters,
    };

    const platformResponse = await postData(`/api/v1/platforms/init`, platformPayload, {
      Authorization: getAuthorizationHeader(),
    });

    if (!platformResponse) {
      setSaving(false);
      Swal.fire(
        "Error!",
        `Something goes wrong on Platform ${platformPayload.name} creation. Please try again later.`,
        "error"
      );
      return;
    }

    const createdPlatform = await resolveCreatedPlatform(platformResponse, platformPayload.name);

    if (!createdPlatform || !createdPlatform.id) {
      setSaving(false);
      Swal.fire(
        "Error!",
        `Platform ${platformPayload.name} was created but cannot be linked to user automatically.`,
        "error"
      );
      return;
    }

    const userPayload = {
      firstName: generatedUserValues.firstName,
      lastName: "Demo",
      username: generatedUserValues.username,
      email: generatedUserValues.email,
      password: userPassword,
      roles: Number(masterClientRole.id),
      platforms: [createdPlatform.id],
    };

    const createUserResponse = await postData(`/api/v1/users/`, userPayload, {
      Authorization: getAuthorizationHeader(),
    });

    if (!createUserResponse) {
      setSaving(false);
      Swal.fire(
        "Error!",
        `Platform ${platformPayload.name} created but user ${userPayload.username} creation failed.`,
        "error"
      );
      return;
    }

    const createdUserId = getCreatedEntityId(createUserResponse);

    if (!createdUserId) {
      setSaving(false);
      Swal.fire(
        "Error!",
        `Platform ${platformPayload.name} created but cannot resolve new user id for permissions setup.`,
        "error"
      );
      return;
    }

    if (masterClientPermissions.length > 0) {
      const permissionsPayload = masterClientPermissions.map((permission) => ({
        id: permission.id,
      }));

      const setPermissionsResponse = await postData(
        `/api/v1/users/${createdUserId}/permissions`,
        permissionsPayload,
        {
          Authorization: getAuthorizationHeader(),
        }
      );

      if (!setPermissionsResponse) {
        setSaving(false);
        Swal.fire(
          "Error!",
          `Platform and user were created, but permissions setup failed for ${userPayload.username}.`,
          "error"
        );
        return;
      }
    }

    const demoTemplateResult = await createDemoTemplateAndFilter(createdPlatform.id);

    if (!demoTemplateResult.ok) {
      setSaving(false);
      resetWizard();

      await Swal.fire(
        "Error!",
        `Platform and user were created, but ${demoTemplateResult.message} Please complete template setup manually.`,
        "error"
      );

      signalCloseModal(true, platformPayload.name);
      return;
    }

    setSaving(false);
    resetWizard();

    const backofficeUrl = ensureTrailingSlash(window.location.origin);
    const docsBaseUrl = process.env.REACT_APP_API_DOCUMENTATION_URL || window.location.origin;
    const docsUrl = `${ensureTrailingSlash(docsBaseUrl)}api_docs`;
    const apiKeyInfo = createdPlatform.api_key
      ? createdPlatform.api_key
      : "Available in Management > Platforms > API Key";

    await Swal.fire({
      title: "Platform & User Created",
      html: buildSuccessHtml({
        backofficeUrl,
        username: userPayload.username,
        password: userPayload.password,
        apiKey: apiKeyInfo,
        docsUrl,
      }),
      width: 900,
      confirmButtonText: "Close",
      confirmButtonColor: "#3085d6",
    });

    signalCloseModal(true, platformPayload.name);
  };

  const getStepTitle = () => {
    if (activeStep === WIZARD_STEPS.PLATFORM) return "Step 1/3: Platform";
    if (activeStep === WIZARD_STEPS.ENTITIES) return "Step 2/3: Setup";
    return "Step 3/3: User";
  };

  return (
    <Dialog open={showModal} onClose={handleClose} fullWidth={true} maxWidth={"md"}>
      <DialogTitle>Create New Platform Wizard</DialogTitle>
      <DialogContent>
        <Card sx={{ p: 2 }}>
          <SoftBox mb={2} display="flex" justifyContent="space-between" alignItems="center">
            <SoftTypography variant="h6">{getStepTitle()}</SoftTypography>
            <SoftTypography variant="caption" color="text">
              Quick create with safe defaults
            </SoftTypography>
          </SoftBox>

          <Grid
            container
            spacing={2}
            style={{ display: activeStep === WIZARD_STEPS.PLATFORM ? "flex" : "none" }}
          >
            <Grid item xs={12}>
              <SoftTypography variant="caption">Platform Name *</SoftTypography>
              <SoftInput
                value={platformName}
                onChange={(event) => setPlatformName(event.target.value)}
                placeholder="eg. testplatform.gr"
              />
            </Grid>

            <Grid item xs={12}>
              <SoftBox
                p={2}
                borderRadius="lg"
                sx={{ border: "1px solid #dadde9", backgroundColor: "#f8f9fa" }}
              >
                <SoftTypography variant="caption" fontWeight="bold">
                  Auto generated from Platform Name
                </SoftTypography>
                <Grid container spacing={1} mt={0.5}>
                  <Grid item xs={12} sm={6}>
                    <SoftTypography variant="caption">Platform Exchange Name</SoftTypography>
                    <SoftInput value={generatedPlatformValues.exchangeName} disabled />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <SoftTypography variant="caption">Queue Identifier</SoftTypography>
                    <SoftInput value={generatedPlatformValues.queueIdentifier} disabled />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <SoftTypography variant="caption">Virtual Court Email</SoftTypography>
                    <SoftInput value={generatedPlatformValues.vlcourtEmail} disabled />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <SoftTypography variant="caption">Virtual Court Password</SoftTypography>
                    <SoftInput value={generatedPlatformValues.vlcourtPassword} disabled />
                  </Grid>
                </Grid>
              </SoftBox>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    color="info"
                    checked={demoFeed}
                    onChange={(event) => setDemoFeed(event.target.checked)}
                  />
                }
                label="Demo Feed"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    color="info"
                    checked={settleOnlyBets}
                    onChange={(event) => setSettleOnlyBets(event.target.checked)}
                  />
                }
                label="Settle only Platform Bets"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    color="info"
                    checked={validateBets}
                    onChange={(event) => setValidateBets(event.target.checked)}
                  />
                }
                label="Validate Platform Bets"
              />
            </Grid>
          </Grid>

          <Grid
            container
            spacing={2}
            style={{ display: activeStep === WIZARD_STEPS.ENTITIES ? "flex" : "none" }}
          >
            <Grid item xs={12}>
              <SoftTypography variant="caption">RMQ (optional)</SoftTypography>
              <SoftSelect
                isMulti
                isClearable={true}
                isLoading={loadingData}
                options={rmqOptions}
                value={selectedRmqs}
                onChange={(options) => setSelectedRmqs(options ? options : [])}
                placeholder="Select RMQs (optional)"
              />
            </Grid>

            <Grid item xs={12}>
              <SoftTypography variant="caption">Providers *</SoftTypography>
              <SoftSelect
                isMulti
                isClearable={false}
                isLoading={loadingData}
                options={providerOptions}
                value={selectedProviders}
                onChange={(options) => setSelectedProviders(options ? options : [])}
                placeholder="Select providers (all sports are auto enabled for inplay/pregame)"
              />
            </Grid>

            <Grid item xs={12}>
              <SoftBox
                p={1.5}
                borderRadius="lg"
                sx={{ border: "1px dashed #dadde9", backgroundColor: "#fff" }}
              >
                <SoftTypography variant="caption" color="text">
                  <Icon sx={{ verticalAlign: "middle", mr: 0.5 }} fontSize="small">
                    info
                  </Icon>
                  Selected providers are sent with all sports enabled and both inplay/pregame set
                  to true.
                </SoftTypography>
              </SoftBox>
            </Grid>
          </Grid>

          <Grid
            container
            spacing={2}
            style={{ display: activeStep === WIZARD_STEPS.USER ? "flex" : "none" }}
          >
            <Grid item xs={12} sm={6}>
              <SoftTypography variant="caption">Username</SoftTypography>
              <SoftInput
                value={generatedUserValues.username}
                disabled
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <SoftTypography variant="caption">Password</SoftTypography>
              <SoftInput
                value={userPassword}
                disabled
              />
            </Grid>

            <Grid item xs={12}>
              <SoftBox
                p={2}
                borderRadius="lg"
                sx={{ border: "1px solid #dadde9", backgroundColor: "#f8f9fa" }}
              >
                <SoftTypography variant="caption" fontWeight="bold">
                  Auto generated user profile
                </SoftTypography>
                <Grid container spacing={1} mt={0.5}>
                  <Grid item xs={12} sm={4}>
                    <SoftTypography variant="caption">First Name</SoftTypography>
                    <SoftInput value={generatedUserValues.firstName} disabled />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <SoftTypography variant="caption">Last Name</SoftTypography>
                    <SoftInput value={generatedUserValues.lastName} disabled />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <SoftTypography variant="caption">Email</SoftTypography>
                    <SoftInput value={generatedUserValues.email} disabled />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <SoftTypography variant="caption">Role</SoftTypography>
                    <SoftInput
                      value={masterClientRole ? masterClientRole.name : "Master client role not found"}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <SoftTypography variant="caption">Permissions</SoftTypography>
                    <SoftInput
                      value={
                        masterClientRole
                          ? `${masterClientPermissions.length} permissions will be assigned`
                          : "No permissions available"
                      }
                      disabled
                    />
                  </Grid>
                </Grid>
              </SoftBox>
            </Grid>
          </Grid>

          <SoftBox mt={3} display="flex" justifyContent="space-between">
            <SoftButton variant="outlined" color="info" onClick={handleClose} disabled={saving}>
              Cancel
            </SoftButton>

            <SoftBox display="flex" gap={1}>
              <SoftButton
                variant="outlined"
                color="dark"
                onClick={handleBack}
                style={{ display: activeStep > WIZARD_STEPS.PLATFORM ? "inline-flex" : "none" }}
                disabled={saving}
              >
                Back
              </SoftButton>

              <SoftButton
                variant="gradient"
                color="dark"
                onClick={handleNext}
                style={{ display: activeStep < WIZARD_STEPS.USER ? "inline-flex" : "none" }}
                disabled={loadingData || saving}
              >
                Next
              </SoftButton>

              <SoftButton
                variant="gradient"
                color="success"
                onClick={handleCreatePlatformAndUser}
                style={{ display: activeStep === WIZARD_STEPS.USER ? "inline-flex" : "none" }}
                disabled={loadingData || saving}
              >
                {saving ? "Saving..." : "Create Platform & User"}
              </SoftButton>
            </SoftBox>
          </SoftBox>
        </Card>
      </DialogContent>
    </Dialog>
  );
}

CreatePlatformWizard.defaultProps = {
  showModal: false,
  signalCloseModal: () => {
    // close modal
  },
};

CreatePlatformWizard.propTypes = {
  showModal: PropTypes.bool,
  signalCloseModal: PropTypes.func,
};

export default CreatePlatformWizard;

